import React, { useContext, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import { AppContext } from '../../App';
import { deleteLabLogo, uploadLabLogo } from '../../clients/lab_client';
import { showToast } from '../../config_and_helpers/helpers';
import { logError, logInfo } from '../../config_and_helpers/logger';
import { isLabAdmin } from '../../utils/userAccountRole';
import {
  LAB_LOGO_ACCEPT,
  hasLabLogo,
  validateLabLogoFile,
} from '../../utils/labLogo';
import { useRefreshCurrentLab } from '../../hooks/useRefreshCurrentLab';
import LabLogoBadge from './LabLogoBadge';
import './LabLogoBadge.css';

const LOG_CONTEXT = 'LabLogoForm';

/**
 * Admin-only lab logo upload and remove controls.
 *
 * @param {Object} props
 * @param {Object|null|undefined} props.lab - Current lab from AppContext.
 */
const LabLogoForm = ({ lab }) => {
  const { token, userDetails, isSupplier, setCurrentLab } = useContext(AppContext);
  const labAdmin = isLabAdmin(userDetails, isSupplier);
  const refreshCurrentLab = useRefreshCurrentLab();
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [fieldError, setFieldError] = useState('');

  if (!lab) {
    return null;
  }

  const busy = isUploading || isRemoving;

  const handlePickFile = () => {
    if (!labAdmin || busy) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !labAdmin) return;

    setFieldError('');
    const validation = validateLabLogoFile(file);
    if (!validation.valid) {
      setFieldError(validation.message);
      return;
    }

    setIsUploading(true);
    logInfo(LOG_CONTEXT, 'upload lab logo submit', { fileName: file.name, fileSize: file.size });
    const result = await uploadLabLogo(token, file);
    setIsUploading(false);

    if (result.success) {
      setCurrentLab(result.data);
      showToast('Lab logo updated.', 'success', 'top-right', 3000);
      logInfo(LOG_CONTEXT, 'upload lab logo success', { labId: result.data?.id });
      return;
    }

    if (result.status === 403) {
      showToast('Only lab Admins can change the lab logo.', 'error', 'top-right', 4000);
      return;
    }

    const message = result.messages?.[0] || 'Failed to upload lab logo.';
    setFieldError(message);
    logError(LOG_CONTEXT, 'upload lab logo failed', {
      status: result.status ?? null,
      messages: result.messages,
    });
  };

  const handleRemove = async () => {
    if (!labAdmin || busy || !hasLabLogo(lab)) return;

    setFieldError('');
    setIsRemoving(true);
    logInfo(LOG_CONTEXT, 'remove lab logo submit');
    const result = await deleteLabLogo(token);
    setIsRemoving(false);

    if (result.success) {
      setCurrentLab(result.data);
      showToast('Lab logo removed.', 'success', 'top-right', 3000);
      logInfo(LOG_CONTEXT, 'remove lab logo success', { labId: result.data?.id });
      return;
    }

    if (result.status === 403) {
      showToast('Only lab Admins can change the lab logo.', 'error', 'top-right', 4000);
      return;
    }

    showToast('Failed to remove lab logo.', 'error', 'top-right', 4000);
    logError(LOG_CONTEXT, 'remove lab logo failed', {
      status: result.status ?? null,
      messages: result.messages,
    });
  };

  return (
    <div className="lab-logo-form">
      <LabLogoBadge lab={lab} size="md" onLogoError={refreshCurrentLab} />

      {labAdmin ? (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept={LAB_LOGO_ACCEPT}
            className="d-none"
            onChange={handleFileChange}
          />
          <div className="lab-logo-form__actions">
            <Button
              type="button"
              variant="outline-primary"
              size="sm"
              disabled={busy}
              onClick={handlePickFile}
            >
              {isUploading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  Uploading…
                </>
              ) : hasLabLogo(lab) ? (
                'Replace logo'
              ) : (
                'Upload logo'
              )}
            </Button>
            {hasLabLogo(lab) ? (
              <Button
                type="button"
                variant="outline-danger"
                size="sm"
                disabled={busy}
                onClick={handleRemove}
              >
                {isRemoving ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-1" />
                    Removing…
                  </>
                ) : (
                  'Remove logo'
                )}
              </Button>
            ) : null}
          </div>
          <p className="glass-page-sub lab-logo-form__hint mb-0">
            JPEG, PNG, WebP, or GIF. Max 2 MB.
          </p>
          {fieldError ? (
            <p className="lab-logo-form__error mb-0">{fieldError}</p>
          ) : null}
        </>
      ) : (
        <p className="glass-page-sub lab-logo-form__hint mb-0">
          Logo is managed by lab Admins.
        </p>
      )}
    </div>
  );
};

export default LabLogoForm;
